import { Col, DatePicker, Layout, Row, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatNumbers,
  getDateRange,
  handleChangePage,
  handleExportToExcel,
  handleExportToPDF,
  handlePageSizeChange,
} from "../../Utils/Common";
import CustomTable from "../../Components/Table/CustomTable";
import { useEffect, useState } from "react";
import { getRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";

const { RangePicker } = DatePicker;
const { Option } = Select;

const SalesReport = () => {
  const [loading, setLoading] = useState("");
  const dateRange = getDateRange("month");
  const [filterConfig, setFilterConfig]: any = useState({
    page: 1,
    date_from: formatDate(dateRange[0], "YYYY-MM-DD"),
    date_to: formatDate(dateRange[1], "YYYY-MM-DD"),
  });
  const [dataSize, setDataSize] = useState(0);
  const [dataSource, setDataSource]: any = useState([]);
  const [dataSummary, setDataSummary]: any = useState([]);
  const [branches, setBranches]: any = useState([]);
  const [vehicleTypes, setVehicleTypes]: any = useState([]);
  const [brands, setBrands]: any = useState([]);

  type SaleRecord = {
    id: number;
    customer_name: string;
    claim_slip_no: string;
    paid_amount: number;
    branch_name: string;
    cashier_name: string;
    payment_mode: string;
    bank_name: string;
    check_date: string;
    check_no: string;
    brand_name: string;
    vehicle_type_name: string;
    agent_name: string;
    added_on: string;
  };

  const columns: ColumnsType<SaleRecord> = [
    {
      title: "Ref No.",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
    },
    {
      title: "Transaction Details",
      key: "transaction_details",
      render: (_, record) => (
        <>
          <div>
            Brand: <b>{record.brand_name}</b>
          </div>
          <div>
            Vehicle Type: <b>{record.vehicle_type_name}</b>
          </div>
          <div>
            Agent: <b>{record.agent_name}</b>
          </div>
        </>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, record: any) => (
        <>
          <div>
            Grand Total: <b>{formatNumbers(record.amount_payable)}</b>
          </div>
          <div>
            Mode: <b>{record.payment_mode}</b>
          </div>
          <div>
            {record.check_date
              ? `Date: ${dayjs(record.check_date).format("MMM DD, YYYY")}`
              : ""}
          </div>
        </>
      ),
    },
    {
      title: "Branch",
      dataIndex: "branch_name",
      key: "branch_name",
    },
    {
      title: "Cashier",
      key: "Cashier",
      render: (_, record) => (
        <>
          <div>{record.cashier_name}</div>
          <div>{dayjs(record.added_on).format("MMM DD, YYYY h:mm A")}</div>
        </>
      ),
    },
  ];

  function handleFilterChange(value: any, field: string) {
    if (field === "dates") {
      setFilterConfig((prev: any) => ({
        ...prev,
        date_from: formatDate(value?.[0], "YYYY-MM-DD"),
        date_to: formatDate(value?.[1], "YYYY-MM-DD"),
      }));
    } else {
      setFilterConfig((prev: any) => ({
        ...prev,
        [field]: value === "" ? null : value,
      }));
    }
  }

  function exportToExcel() {
    handleExportToExcel(
      dataSource.map((record: any) => ({
        ...record,
        added_on: dayjs(record.added_on).format("MMM DD, YYYY h:mm A"),
        check_date: record.check_date
          ? `Date: ${dayjs(record.check_date).format("MMM DD, YYYY")}`
          : "",
        amount_payable: formatNumbers(record.amount_payable),
      })),
      [
        {
          title: "Ref No.",
          dataIndex: "id",
          key: "id",
        },
        {
          title: "Customer",
          dataIndex: "customer_name",
          key: "customer_name",
        },
        {
          title: "Brand",
          dataIndex: "brand_name",
          key: "brand_name",
        },
        {
          title: "Vehicle Type",
          dataIndex: "vehicle_type_name",
          key: "vehicle_type_name",
        },
        {
          title: "Agent",
          dataIndex: "agent_name",
          key: "agent_name",
        },
        {
          title: "Grand Total",
          dataIndex: "amount_payable",
          key: "amount_payable",
        },
        {
          title: "Mode",
          dataIndex: "payment_mode",
          key: "payment_mode",
        },
        {
          title: "Branch",
          dataIndex: "branch_name",
          key: "branch_name",
        },
        {
          title: "Cashier",
          dataIndex: "cashier_name",
          key: "cashier_name",
        },
        {
          title: "Date",
          dataIndex: "added_on",
          key: "added_on",
        },
      ],
      "SalesReport",
    );
  }

  function exportToPDF() {
    handleExportToPDF(
      dataSource,
      columns,
      "SalesReport.pdf",
      {
        title: "Sales Report",
        content: `Generated on ${new Date().toLocaleString()}`,
      },
      `Total Records: ${dataSource.length}`,
    );
  }

  async function fetchBranches() {
    setBranches([]);
    setLoading("fetch");
    try {
      const response = await getRequest(`branches/get`, {}, true);
      if (response?.success) {
        var data =
          response.data?.data?.map((info: any) => ({
            ...info,
            branch_id: info.id,
            branch_name: info.name,
          })) || [];
        setBranches(data);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    } finally {
      setLoading("");
    }
  }

  async function fetchVehicleTypes() {
    setVehicleTypes([]);
    setLoading("fetch");
    var response = await getRequest(`vehicle_types/get`, {}, true);
    if (response.success) {
      var data = response.data.data.map((info: any) => ({
        ...info,
        vehicle_type_id: info.id,
        vehicle_name: info.name,
      }));
      setVehicleTypes(data);
    }
    setLoading("");
  }

  async function fetchBrands() {
    setBrands([]);
    setLoading("fetch");
    var response = await getRequest(`brands/get`, {}, true);
    if (response.success) {
      var data = response.data.data.map((info: any) => ({
        ...info,
        brand_id: info.id,
        brand_name: info.name,
      }));
      setBrands(data);
    }
    setLoading("");
  }

  const fetchTableData = async () => {
    setDataSource([]);
    setLoading("fetching");
    try {
      const response = await getRequest("reports/get_sales", filterConfig);
      if (response?.success) {
        const responseData = response.data?.data || [];
        const totalCount = response.data?.total || response.data?.size || 0;
        const summary = response.data?.summary || {
          cash_sales: 0,
          check_sales: 0,
          credit_sales: 0,
        };

        setDataSource(Array.isArray(responseData) ? responseData : []);
        setDataSize(totalCount);
        setDataSummary(summary);
      } else {
        setDataSource([]);
        setDataSize(0);
        setDataSummary({ cash_sales: 0, check_sales: 0, credit_sales: 0 });
      }
    } catch (error) {
      console.error("Error fetching sales report:", error);
    } finally {
      setLoading("");
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchVehicleTypes();
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  return (
    <Layout className="page">
      <Helmet>
        <title>VAR CTPL Insurance System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Sales Report"
              withDateRangeFilter={false}
              withAddButton={false}
              withExportDropdownButton={true}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={() => exportToExcel()}
              onExportToPDF={() => exportToPDF()}
              withSearchBtn={true}
              searchPlaceholder="Search customer/agent/cashier"
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value,
                }))
              }
            />
          </Col>
          <Col xs={24} md={12} lg={8} className="mb-1">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col span={24} className="mb-3">
            <Row>
              <Col xs={24} md={12} lg={6} className="mb-2 me-2 mb-lg-0">
                <RangePicker
                  value={[
                    filterConfig.date_from
                      ? dayjs(filterConfig.date_from)
                      : null,
                    filterConfig.date_to ? dayjs(filterConfig.date_to) : null,
                  ]}
                  format="MMMM DD, YYYY"
                  onChange={(e) => handleFilterChange(e, "dates")}
                  allowClear
                  className="w-full"
                />
              </Col>
              <Col xs={24} md={12} lg={5} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  showSearch
                  placeholder="Select branch"
                  value={filterConfig.branch_id || ""}
                  onChange={(value) => handleFilterChange(value, "branch_id")}
                  allowClear
                  filterOption={(input: string, option?: any) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="">All Branches</Option>
                  {branches.map((branch: any) => (
                    <Option key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch_name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12} lg={3} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  showSearch
                  placeholder="Select brand"
                  value={filterConfig.brand_id || ""}
                  onChange={(value) => handleFilterChange(value, "brand_id")}
                  allowClear
                  filterOption={(input: string, option?: any) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="">All Brands</Option>
                  {brands.map((brand: any) => (
                    <Option key={brand.brand_id} value={brand.brand_id}>
                      {brand.brand_name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12} lg={5} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  placeholder="Select vehicle type"
                  value={filterConfig.vehicle_type_id || ""}
                  onChange={(value) =>
                    handleFilterChange(value, "vehicle_type_id")
                  }
                  allowClear
                >
                  <Option value="">All Vehicle Types</Option>
                  {vehicleTypes.map((info: any) => (
                    <Option
                      key={info.vehicle_type_id}
                      value={info.vehicle_type_id}
                    >
                      {info.vehicle_name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12} lg={3} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  placeholder="Select MOP"
                  value={filterConfig.mop || ""}
                  onChange={(value) => handleFilterChange(value, "mop")}
                  allowClear
                >
                  <Option value="">All MOP</Option>
                  <Option value="cash">Cash</Option>
                  <Option value="credit_customer">Credit Customer</Option>
                </Select>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <div className="table-container">
              <Row className="mb-3 mx-2">
                <Col xs={24} lg={8} className="font-bold">
                  Cash Sales: {formatNumbers(dataSummary?.cash_sales || 0)}
                </Col>
                <Col xs={24} lg={8} className="font-bold">
                  Check Sales: {formatNumbers(dataSummary?.check_sales || 0)}
                </Col>
                <Col xs={24} lg={8} className="font-bold">
                  Credit Sales: {formatNumbers(dataSummary?.credit_sales || 0)}
                </Col>
              </Row>
              <CustomTable
                data={dataSource}
                columns={columns}
                rowKey="id"
                loading={loading === "fetching"}
                size="small"
                scroll={{ x: "1000px" }}
                pagination={{
                  current: filterConfig.page,
                  total: dataSize,
                  showSizeChanger: true,
                  onChange: (page: number) =>
                    handleChangePage(page, setFilterConfig),
                  onShowSizeChange: (_: number, size: number) =>
                    handlePageSizeChange(size, setFilterConfig),
                }}
                handleRowClick={(record: any) => {}}
              />
            </div>
          </Col>
        </Row>
      </Layout>
      <PersistentFooter />
    </Layout>
  );
};

export default SalesReport;
